import { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check,
  Circle,
  Lock,
  FileText,
  Settings,
  Sparkles,
  PencilLine,
  Music,
  Download,
  AudioLines
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useSessionStore } from "@/hooks/useSessionStore";

type StepId =
  | "project_selection"
  | "project_details"
  | "audio_sources"
  | "transcription_review"
  | "scenario_review"
  | "scenario_edit"
  | "final_validation"
  | "configuration_scenario"
  | "choix_scenario"
  | "edition_text"
  | "synthese_vocale";

interface CustomStep {
  id: StepId;
  title: string;
  subtitle: string;
  icon: React.ComponentType<any>;
}

const CUSTOM_STEPS: CustomStep[] = [
  {
    id: "configuration_scenario",
    title: "Configurations des scénarios",
    subtitle: "Sélectionner plusieurs paramètres de générations pour vos scénarios.",
    icon: Settings
  },
  {
    id: "choix_scenario",
    title: "Choix du scénario",
    subtitle: "Sélectionner le scénario que vous souhaitez transformer en artefact.",
    icon: Sparkles
  },
  {
    id: "edition_text",
    title: "Édition du scénario",
    subtitle: "Modifier et baliser le scénario pour générer un audio.",
    icon: PencilLine
  },
  {
    id: "synthese_vocale",
    title: "Synthèse vocale",
    subtitle: "Sélectionner le modèle de voix et la voix de narration.",
    icon: AudioLines
  },
  {
    id: "scenario_edit",
    title: "Édition de l'audio",
    subtitle: "Personnaliser l'audio pour le transformer en artefact.",
    icon: Music
  },
  {
    id: "final_validation",
    title: "Exports",
    subtitle: "Finaliser et valider l'artefact pour le télécharger.",
    icon: Download
  }
];

export function StepNavigator() {
  const {
    steps,
    currentStep,
    setCurrentStep,
    language,
    progress,
    maxReachedStepIndex,
    setMaxReachedStepIndex
  } = useSessionStore();
  const navigate = useNavigate();

  const customCurrentIndex = CUSTOM_STEPS.findIndex((s) => s.id === currentStep);

  useEffect(() => {
    if (customCurrentIndex !== -1 && customCurrentIndex > maxReachedStepIndex) {
      setMaxReachedStepIndex(customCurrentIndex);
    }
  }, [customCurrentIndex, maxReachedStepIndex, setMaxReachedStepIndex]);

  const ordered = useMemo(
    () => steps.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [steps]
  );

  const currentIndex = ordered.findIndex((s) => s.id === currentStep);

  const isEnabled = (stepId: string): boolean => {
    switch (stepId) {
      case "project_selection":
      case "project_details":
        return true;
      case "audio_sources":
        return true;
      case "transcription_review":
        return progress.audioReady;
      case "configuration_scenario":
        return progress.audioReady;
      case "scenario_review":
        return progress.transcriptionsReviewed;
      case "choix_scenario":
        return progress.scenariosReady;
      case "scenario_edit":
        return progress.scenariosReady && progress.scenarioChosen;
      case "edition_text":
        return progress.scenarioChosen;
      case "synthese_vocale":
        return progress.scenarioChosen;
      case "final_validation":
        return progress.scenarioEdited;
      default:
        return true;
    }
  };

  const handleStepClick = (stepId: string) => {
    if (isEnabled(stepId)) {
      setCurrentStep(stepId as StepId);
      navigate(`/step/${stepId}`);
    }
  };

  const handleRetour = () => {
    if (currentStep === "configuration_scenario") {
      setCurrentStep("project_details");
      navigate("/step/project_details");
    } else {
      const idx = CUSTOM_STEPS.findIndex((s) => s.id === currentStep);
      if (idx > 0) {
        const prevStep = CUSTOM_STEPS[idx - 1].id;
        setCurrentStep(prevStep);
        navigate(`/step/${prevStep}`);
      }
    }
  };

  const getNextStepId = (): StepId | null => {
    const idx = CUSTOM_STEPS.findIndex((s) => s.id === currentStep);
    if (idx !== -1 && idx < CUSTOM_STEPS.length - 1) {
      return CUSTOM_STEPS[idx + 1].id;
    }
    return null;
  };

  const nextStepId = getNextStepId();
  const isSuivantEnabled = nextStepId ? isEnabled(nextStepId) : false;

  const handleSuivant = () => {
    if (isSuivantEnabled && nextStepId) {
      setCurrentStep(nextStepId);
      navigate(`/step/${nextStepId}`);
    }
  };

  // 1. Project details page shows custom Transcription single step
  if (currentStep === "project_details") {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="pb-4 border-b border-border mb-4 text-center">
          <h3 className="text-[18px] font-bold text-[#0F172B] tracking-tight">
            Création d'un artefact
          </h3>
        </div>

        {/* Transcription block */}
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-3 rounded-xl border border-[#007AFF] bg-[#EFF6FF] p-4 text-left">
            <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-[#007AFF] bg-white">
              <div className="h-2.5 w-2.5 rounded-full bg-[#007AFF]" />
            </div>

            <div className="flex flex-col gap-1 min-w-0">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#0F172B] shrink-0" />
                <span className="text-[16px] font-semibold text-[#0F172B]">Transcription</span>
              </div>
              <p className="text-[12px] text-[#45556C] leading-normal font-normal">
                Lire et modifier la transcription des fichiers audios.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 2. Custom 5-step flow for artefact creation
  const isCustomFlow = CUSTOM_STEPS.some((s) => s.id === currentStep);
  if (isCustomFlow) {
    return (
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex flex-col gap-3">
          <h3 className="text-[18px] font-bold text-[#0F172B] tracking-tight text-center">
            Création d'un artefact
          </h3>
        </div>

        <div className="border-b border-border my-1" />

        {/* Custom Steps Cards */}
        <div className="flex flex-col gap-3">
          {CUSTOM_STEPS.map((step, index) => {
            const isCurrent = step.id === currentStep;
            const isDone = !isCurrent && index < maxReachedStepIndex;
            const enabled = isEnabled(step.id);
            const isClickable = (index <= customCurrentIndex || index <= maxReachedStepIndex) && enabled;
            const Icon = step.icon;

            return (
              <button
                key={step.id}
                disabled={!isClickable}
                onClick={() => handleStepClick(step.id)}
                className={cn(
                  "group flex items-start gap-3 rounded-xl border p-4 text-left transition-all w-full",
                  isCurrent
                    ? "border-[#007AFF] bg-[#EFF6FF] text-[#0F172B]"
                    : isClickable
                      ? "border-[#E2E8F0] hover:border-[#007AFF] bg-white text-[#45556C] cursor-pointer shadow-sm"
                      : "border-transparent bg-[#F8FAFC] text-[#94A3B8] cursor-not-allowed"
                )}
              >
                {/* Left Bullet Indicator */}
                <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center">
                  {isDone ? (
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#CBD5E1] text-white">
                      <Check className="h-3 w-3" />
                    </div>
                  ) : isCurrent ? (
                    <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-[#007AFF] bg-white">
                      <div className="h-2.5 w-2.5 rounded-full bg-[#007AFF]" />
                    </div>
                  ) : !enabled ? (
                    <div className="flex h-5 w-5 items-center justify-center rounded-full border border-[#CBD5E1] bg-[#F8FAFC] text-[#94A3B8]">
                      <Lock className="h-2.5 w-2.5" />
                    </div>
                  ) : (
                    <div className="h-5 w-5 rounded-full border border-[#CBD5E1] bg-white" />
                  )}
                </div>

                {/* Content */}
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon
                      className={cn(
                        "h-5 w-5 shrink-0",
                        isCurrent ? "text-[#007AFF]" : isClickable ? "text-[#007AFF]" : "text-[#94A3B8]"
                      )}
                    />
                    <span
                      className={cn(
                        "text-[16px] font-semibold leading-snug",
                        isCurrent ? "text-[#0F172B]" : isClickable ? "text-[#0F172B] group-hover:text-[#0051B3]" : "text-[#94A3B8]"
                      )}
                    >
                      {step.title}
                    </span>
                  </div>
                  <p
                    className={cn(
                      "text-[12px] leading-normal font-normal",
                      isCurrent ? "text-[#45556C]" : isClickable ? "text-[#64748B]" : "text-[#CBD5E1]"
                    )}
                  >
                    {step.subtitle}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // 3. Fallback standard list
  return (
    <nav className="flex flex-col gap-1">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-2">
        Étapes
      </p>
      {ordered.map((step, index) => {
        const isCurrent = step.id === currentStep;
        const isDone = currentIndex > -1 && index < currentIndex;
        const enabled = isEnabled(step.id);

        return (
          <button
            key={step.id}
            disabled={step.id !== currentStep}
            onClick={() => {
              if (step.id !== currentStep) return;
              setCurrentStep(step.id);
              navigate(step.id === ordered[0]?.id ? "/" : `/step/${step.id}`);
            }}
            className={cn(
              "group flex items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-all",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isCurrent && "bg-primary/10 text-primary",
              isDone && !isCurrent && "text-success hover:bg-success-muted/70",
              !isCurrent && !isDone && enabled && "text-foreground hover:bg-accent",
              step.id !== currentStep && "cursor-not-allowed"
            )}
          >
            <span
              className={cn(
                "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors",
                isCurrent && "border-primary bg-primary text-primary-foreground",
                isDone && !isCurrent && "border-success bg-success text-success-foreground",
                !isCurrent && !isDone && "border-border bg-background text-muted-foreground"
              )}
            >
              {isDone ? (
                <Check className="h-3 w-3" />
              ) : !enabled ? (
                <Lock className="h-2.5 w-2.5" />
              ) : (
                <Circle className="h-2 w-2 fill-current" />
              )}
            </span>

            <span className="flex flex-col gap-0.5 min-w-0">
              <span className="text-sm font-medium leading-tight truncate">
                {step.name?.[language] ?? step.id}
              </span>
              <span className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                {step.description?.[language]}
              </span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}
